import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../utils/ThemeSwitcher';
import AuroraBackground from '../../pages/AuroraBackground';
import logoSource from '../../assets/dataquadio.png';
import logoSourceInverted from '../../assets/dataquadio_inverted.png';
import nobleLogo from '../../assets/noble_syntax.png'
import WelcomeLanding from './WelcomeLanding';
import UserDock from './UserDock';
import { 
  FolderOpen, 
  TableIcon, 
  ListChecks, 
  GitCompare, 
  Users,
  Settings,
  Brain,
  Layout,
  Zap,
  CheckCircle2,
  Download,
  FileText,
  Moon,
  Sun,
  LogOut,
  Database,
  FileSpreadsheet,
  ClipboardCheck,
  Menu,
  X,
  HelpCircle,
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Compass,
  Shield,
  ArrowRight,
  Link2,
  Check,
  BarChart3,
  Headphones
} from 'lucide-react';
 
 
// 0. RESPONSIVE HOOK
 
 
const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });
  
  const [isTouchDevice, setIsTouchDevice] = useState(false);
 
  useEffect(() => {
    // Detect touch device
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches
      );
    };
    
    checkTouch();
    
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
 
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
 
  // More aggressive mobile detection:
  // - Under 900px width, OR
  // - Touch device under 1024px width (catches phones in landscape and tablets)
  const isMobile = windowSize.width < 900 || (isTouchDevice && windowSize.width < 1024);
  const isTablet = !isMobile && windowSize.width >= 900 && windowSize.width < 1200;
  const isDesktop = windowSize.width >= 1200;
 
  return {
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    width: windowSize.width,
    height: windowSize.height,
  };
};
 
 
// 0b. AUTO-SCALE HOOK
// Measures a container's width and returns a scale factor so the preview
// content fills the width while keeping native aspect ratio.
//
// Implementation notes:
//   - The ref'd element is conditionally rendered (preview shows/hides
//     based on activeModal/previewContent). React reassigns ref.current
//     when it remounts, but our effect's closure can hold a stale
//     reference. So we re-read ref.current FRESH on every tick.
//   - We poll on both web and Tauri. Web alone could rely on RO, but a
//     400ms setInterval with React's setState bailout (Object.is) is
//     essentially free when stable, and it's the only thing that catches
//     the conditional-remount case AND the WebView2 RO-drop case.
//   - We re-attach the ResizeObserver when the element identity changes
//     so we still get fast updates from drag-resize on web.
 
const useAutoScale = (baseWidth = 1600, minScale = 0.45, maxScale = 0.9) => {
  const ref = React.useRef(null);
  const [scale, setScale] = React.useState(0.65);
 
  React.useEffect(() => {
    let ro = null;
    let observedEl = null;
 
    const update = () => {
      const el = ref.current; // FRESH each tick — never trust the closure
      if (!el) return;
 
      // Re-attach RO if the element was remounted
      if (typeof ResizeObserver !== 'undefined' && el !== observedEl) {
        if (ro) ro.disconnect();
        ro = new ResizeObserver(update);
        ro.observe(el);
        observedEl = el;
      }
 
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        const next = Math.max(minScale, Math.min(maxScale, w / baseWidth));
        setScale(next);
      }
    };
 
    update();
    const pollId = setInterval(update, 400);
 
    return () => {
      if (ro) ro.disconnect();
      clearInterval(pollId);
    };
  }, [baseWidth, minScale, maxScale]);
 
  return [ref, scale];
};
 
 
// 1. CLEAN STAGE ICON COMPONENT
// Replaces animated SVG mascot with professional icon + colored background
 
const StageIcon = ({ 
  icon: Icon,
  color = '#4FC3F7', 
  isActive = false,
  isHovered = false,
  size = 28
}) => {
  const { isDarkMode } = useTheme();
  const iconSize = Math.round(size * 0.5);
  const grayColor = isDarkMode ? '#6d7178' : '#8a8f98';
  const displayColor = isActive ? color : isHovered ? color : grayColor;
  
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: Math.round(size * 0.32),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isActive 
        ? (isDarkMode ? `${color}1f` : `${color}14`)
        : isHovered 
          ? (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
          : (isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(0,0,0,0.045)'),
      border: `1px solid ${isActive ? `${color}59` : isHovered ? `${color}40` : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)')}`,
      boxShadow: isActive
        ? (isDarkMode ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.06), 0 1px 6px rgba(0,0,0,0.05)')
        : 'none',
      transition: 'all 0.25s ease',
      flexShrink: 0,
    }}>
      <Icon 
        size={iconSize} 
        strokeWidth={1.75}
        color={isActive ? color : displayColor}
        style={{ transition: 'color 0.25s ease' }}
      />
    </div>
  );
};
 
 
// 2. PLAYFUL ICONS MAPPING
// Using clean lucide icons per pipeline stage
 
 
// 2. STAGE ICONS MAPPING
// Clean lucide icons with colored backgrounds per pipeline stage
 
const StageIcons = {
  setup: (props) => <StageIcon {...props} icon={FolderOpen} color="#2563EB" />,
  dataLoader: (props) => <StageIcon {...props} icon={Database} color="#2563EB" />,
  explore: (props) => <StageIcon {...props} icon={ListChecks} color="#2563EB" />,
  qualityAnalysis: (props) => <StageIcon {...props} icon={Brain} color="#2563EB" />,
  dataCleaning: (props) => <StageIcon {...props} icon={Zap} color="#2563EB" />,
  cleanedData: (props) => <StageIcon {...props} icon={CheckCircle2} color="#2563EB" />,
  exportsReports: (props) => <StageIcon {...props} icon={Download} color="#2563EB" />,
};
 
 
// 3. SIMPLE PIPELINE CONNECTOR
// Clean static line — no particles, no intervals
 
const PipelineConnector = ({ isEnabled, isActive, height = 60, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  const lineColor = isActive 
    ? '#4FC3F7' 
    : isEnabled 
      ? (isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)')
      : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)');
 
  return (
    <div style={{
      position: 'absolute',
      top: '25px',
      left: '20px',
      width: '2px',
      height: `${height}px`,
      background: isEnabled 
        ? `linear-gradient(to bottom, ${lineColor}, ${lineColor}40)`
        : lineColor,
      borderRadius: '1px',
      zIndex: 0,
      ...(isEnabled ? {} : { 
        backgroundImage: `repeating-linear-gradient(to bottom, ${lineColor} 0px, ${lineColor} 4px, transparent 4px, transparent 10px)`,
        background: undefined,
      }),
    }} />
  );
};
 
 
// 4. CONTEXTUAL DOCUMENTATION
// Refined Glassmorphism Card
 
 
const ContextualDocumentation = ({ modalId, isDarkMode, colors, isDetailed = false }) => {
  const documentationContent = {
    'projects': { title: 'Project Management', icon: '📁', description: 'Create and manage your data quality projects.', tips: [{ icon: '➕', text: 'Click "New Project" to start a fresh analysis' }, { icon: '📂', text: 'Select an existing project to continue your work' }], proTip: 'Name your projects descriptively (e.g., "Baseline Survey 2024") for easy identification.',
      detailed: {
        overview: 'Projects are the top-level container for all your data quality work. Each project holds your uploaded datasets, configured checks, results, cleaning scripts, and reports — all in one place.',
        sections: [
          { heading: 'Project Statuses', text: 'Active projects are in progress. Closed projects are archived but still accessible. Team projects are shared with your organization members.' },
          { heading: 'What Gets Saved', text: 'Your primary dataset, XLSForm, back-check data, detector configurations, quality check results, cleaning scripts with version history, and AI assessment results are all persisted per project.' },
          { heading: 'Workflow', text: 'Create a project → Load data (LOAD section) → Explore your data (EXPLORE) → Configure and run checks (ANALYSE) → Clean issues (CLEAN) → Compare and validate (ASSURE) → Export results (EXPORT).' },
        ]
      }
    },
    'primary-survey': { title: 'Load Primary Survey Data', icon: '📊', description: 'Import your main survey dataset for quality analysis.', tips: [{ icon: '📁', text: 'Drag & drop or click to upload CSV/Excel files' }, { icon: '🔢', text: 'Column types are auto-detected (numeric, text, date)' }], proTip: 'Ensure your data has unique respondent IDs for accurate duplicate detection.',
      detailed: {
        overview: 'This is where you load your main survey dataset — the data that all quality checks will run against. DataQuad supports multiple file formats and can connect directly to SurveyCTO.',
        sections: [
          { heading: 'Supported Formats', text: 'Excel (.xlsx, .xls), CSV (.csv), and Stata (.dta). Files can be uploaded via drag-and-drop or file browser. Maximum file size is 100MB.' },
          { heading: 'SurveyCTO Integration', text: 'Connect directly to SurveyCTO with your server name, username, and password. Requires a paid SurveyCTO subscription with API access enabled. You can browse available forms and pull data directly.' },
          { heading: 'Column Type Detection', text: 'DataQuad auto-detects column types: integer, float, string/text, timestamp, and date. You can review detected types in Data Preview. Correct type detection is important — outlier checks only work on numeric columns, and duration checks need timestamps or numerics.' },
          { heading: 'Key Variables', text: 'After loading, you\'ll want to identify your Respondent ID (unique record identifier), Data Collector ID (enumerator field), and Interview Date columns. These enable per-collector analysis and time-series trends.' },
        ]
      }
    },
    'xlsform': { title: 'Load XLSForm', icon: '📋', description: 'Import your survey instrument for skip logic validation.', tips: [{ icon: '📄', text: 'Upload your XLSForm (.xlsx) with survey & choices sheets' }, { icon: '🔗', text: 'Variables are auto-matched to your dataset columns' }], proTip: 'XLSForm is optional but highly recommended for survey flow analysis.',
      detailed: {
        overview: 'Loading your XLSForm survey instrument unlocks skip-logic-aware quality checks. DataQuad parses relevance conditions and constraints to give smarter, more accurate results.',
        sections: [
          { heading: 'What It Enables', text: 'Completeness checks distinguish between truly missing values and correctly skipped questions (based on relevance conditions). Outlier detection uses form-defined constraints (e.g., "age must be between 0 and 120") before falling back to statistical methods.' },
          { heading: 'Variable Matching', text: 'DataQuad automatically matches XLSForm field names to your dataset column names. If names differ (e.g., the form uses "hh_income" but the data has "household_income"), you can manually map them.' },
          { heading: 'Requirements', text: 'Standard XLSForm format with "survey" and "choices" sheets. The survey sheet should include name, type, label, relevant, and constraint columns.' },
        ]
      }
    },
    'backcheck-dataset': { title: 'Load Back-Check Data', icon: '🔍', description: 'Import verification survey data for quality comparison.', tips: [{ icon: '📊', text: 'Upload your back-check/verification survey data' }, { icon: '🔗', text: 'Must have matching respondent IDs with primary data' }], proTip: 'Back-check data helps identify enumerator fabrication and response inconsistencies.',
      detailed: {
        overview: 'Back-check (verification) surveys are re-interviews of a subset of respondents by different enumerators. Comparing responses helps identify data fabrication, misunderstanding, or systematic errors.',
        sections: [
          { heading: 'How Matching Works', text: 'DataQuad merges primary and back-check datasets on a shared respondent ID. Records that match are compared variable-by-variable. Unmatched records are reported separately.' },
          { heading: 'Same Formats', text: 'Supports the same file types as primary data (Excel, CSV, Stata). The back-check dataset should contain the same variable names as the primary survey for the fields you want to compare.' },
          { heading: 'What Comes Next', text: 'After loading, go to Configure Back-Checks to categorize your variables into Type 1 (demographics), Type 2 (complex questions), and Type 3 (stability/opinion) and set tolerance ranges.' },
        ]
      }
    },
    'datepreview': { title: 'Data Preview', icon: '👁️', description: 'Explore your raw data in a sortable, searchable table.', tips: [{ icon: '🔍', text: 'Search across all columns using the search bar' }, { icon: '↕️', text: 'Click column headers to sort data' }], proTip: 'Use preview to spot obvious data issues before running automated checks.',
      detailed: {
        overview: 'A full interactive table view of your dataset. Use it to visually inspect data before configuring automated checks — you\'ll often spot obvious problems (blank columns, garbled text, wrong formats) at a glance.',
        sections: [
          { heading: 'Features', text: 'Full-text search across all columns, column sorting (click headers), and pagination for large datasets. Column types are shown in the header row.' },
          { heading: 'When to Use', text: 'Right after loading data, check that columns loaded correctly, values look reasonable, and your key ID fields (respondent ID, collector ID, date) are populated.' },
        ]
      }
    },
    'data-summary': { title: 'Data Summary', icon: '📈', description: 'View statistical summaries and distributions.', tips: [{ icon: '📊', text: 'See mean, median, std dev for numeric columns' }, { icon: '📝', text: 'View unique values and frequencies for text columns' }], proTip: 'Check for unexpected patterns like uniform distributions or suspicious outliers.',
      detailed: {
        overview: 'Generates descriptive statistics for every variable in your dataset — numeric summaries (mean, median, standard deviation, min, max, quartiles) and categorical breakdowns (unique values, frequencies, mode).',
        sections: [
          { heading: 'Numeric Variables', text: 'Shows count, mean, std, min, 25th percentile, median, 75th percentile, max, and number of missing values.' },
          { heading: 'Text/Categorical Variables', text: 'Shows count, unique values, most frequent value (mode), frequency of the mode, and missing count.' },
          { heading: 'Red Flags to Watch For', text: 'Suspiciously low standard deviation (possible fabrication), unexpected min/max values (data entry errors), very high missing rates, or a single value dominating a categorical variable.' },
        ]
      }
    },
    'correlation-matrix': { title: 'Correlation Matrix', icon: '🔗', description: 'Explore relationships between numeric variables.', tips: [{ icon: '🎨', text: 'Colors indicate correlation strength (-1 to +1)' }, { icon: '🔴', text: 'Red = negative correlation, Blue = positive' }], proTip: 'High correlations between unrelated variables may indicate data fabrication.',
      detailed: {
        overview: 'Displays Pearson correlation coefficients between all pairs of numeric variables as a color-coded heatmap. Values range from -1 (perfect negative) to +1 (perfect positive).',
        sections: [
          { heading: 'Interpreting Results', text: '±0.7 to ±1.0 = strong, ±0.4 to ±0.7 = moderate, ±0.0 to ±0.4 = weak. The diagonal is always 1.0 (a variable correlates perfectly with itself).' },
          { heading: 'Data Quality Use', text: 'Unexpectedly high correlations between unrelated variables (e.g., household size and distance to market = 0.95) can signal enumerator fabrication. Enumerators who invent data often create unrealistic relationships.' },
        ]
      }
    },
    'collector-dashboard': { title: 'Collector Dashboard', icon: '👥', description: 'Analyze data quality by enumerator/collector.', tips: [{ icon: '📊', text: 'Compare performance metrics across collectors' }, { icon: '⚠️', text: 'Flag collectors with unusual patterns' }], proTip: 'Consistently low variation in one collector\'s data may indicate shortcuts.',
      detailed: {
        overview: 'Breaks down data quality metrics by enumerator/data collector. This is one of the most powerful views for identifying problematic fieldwork — patterns that look normal in aggregate often stand out when viewed per-collector.',
        sections: [
          { heading: 'What to Look For', text: 'Collectors with consistently very short interview durations, unusually low variance in responses, very high or very low missing rates compared to peers, or duplicate rate spikes.' },
          { heading: 'Requires', text: 'A Data Collector ID variable must be set when loading your primary data. All quality checks will automatically include per-collector breakdowns when this is configured.' },
        ]
      }
    },
    'audio-audit': { title: 'Audio Audit', icon: '🎧', description: 'Listen to interview recordings to verify enumerator behavior and response quality.', tips: [{ icon: '🎙️', text: 'Defaults to audio_audit (full interview recording); switch to any audio column' }, { icon: '⚡', text: 'Playback at 1.25–2× speed for fast review' }], proTip: 'Available in DataQuad Desktop only — audio files stay on your machine.',
      detailed: {
        overview: 'Audio Audit is a dedicated workspace for reviewing interview recordings. It auto-detects audio columns in your data (audio_audit, response audio fields, test recordings) and resolves filenames to your local media folder. The reviewer can listen to full interviews to verify enumerator behavior, probing depth, question fidelity, and response quality.',
        sections: [
          { heading: 'Two audio types', text: 'Full-interview audit recordings (audio_audit field, ~20–60 min) for enumerator surveillance, plus per-question response audio (e.g. bundle_roster_audio, ~5–30s each) for verifying transcripts. Default is the full interview; switch via the audio column selector.' },
          { heading: 'Submission list', text: 'The left pane lists every submission with its enumerator, firm, date, and survey duration. Search filters across all metadata fields. The reviewer picks one submission at a time, listens, and moves on.' },
          { heading: 'Keyboard shortcuts', text: 'Space (play/pause), J/L (skip ±10s), ←/→ (skip ±5s), comma/period (speed slower/faster), M (mute). All while reading metadata or comparing to transcripts on screen.' },
          { heading: 'Setup', text: 'On first use you point Audio Audit at your local media folder (e.g. the SurveyCTO export "media\\" directory). The path is saved per-project so you only set it once.' },
        ]
      }
    },
    'quality-checks': { title: 'Configure Detectors', icon: '⚙️', description: 'Select and configure data quality checks to run.', tips: [{ icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none"><rect x="2" y="2" width="20" height="20" rx="4" fill="#2EC4E8"/><polyline points="5.5,12.5 9.5,16.5 18.5,7.5" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>', text: 'Enable/disable specific quality checks' }, { icon: '📝', text: 'Select variables to include in each check' }], proTip: 'Start with all checks enabled, then fine-tune based on your survey design.',
      detailed: {
        overview: 'This is where you select which quality checks to run and which variables to include in each. DataQuad offers 7 detectors, each targeting a different dimension of data quality.',
        sections: [
          { heading: 'Uniqueness', text: 'Detects duplicate values across selected variables. Select your key identifiers (respondent ID, phone number, name). A duplicate is any value appearing more than once — the excess count is reported.' },
          { heading: 'Completeness', text: 'Analyzes missing values. Select variables that should always be filled. If you loaded an XLSForm, skip logic is respected — correctly skipped fields are not counted as missing.' },
          { heading: 'Outliers', text: 'Flags values more than 3 standard deviations from the mean (Z-score method). Only works on numeric variables. With XLSForm, form-defined constraints are used first (e.g., "age between 0–120").' },
          { heading: 'Duration', text: 'Analyzes interview timing. Select a pre-calculated duration field, or a start+end timestamp pair. Set your expected min/max range (e.g., 15–90 minutes). Interviews outside this range are flagged as too short or too long.' },
          { heading: 'Survey Flow', text: 'Visualizes response patterns between ordered variables as a Sankey-style flow diagram. Select 2+ variables in logical survey order. Useful for spotting impossible skip patterns.' },
          { heading: 'Geolocation', text: 'Maps interview GPS locations. Select exactly 2 variables: latitude and longitude (must be numeric/float). Identifies spatial clustering and outlier locations.' },
          { heading: 'Text Analysis', text: 'Clusters open-ended text responses to find themes, outliers, and suspiciously similar answers. Select text/string variables. Configurable similarity threshold (default 85%).' },
        ]
      }
    },
    'dashboard': { title: 'Detective Dashboard', icon: '🔎', description: 'Review quality check results and identified issues.', tips: [{ icon: '🏷️', text: 'Switch between check types using tabs' }, { icon: '📊', text: 'View summary statistics and issue counts' }], proTip: 'Focus on high-severity issues first, then work through medium and low.',
      detailed: {
        overview: 'The main results view after running quality checks. Results are organized by check type (tabs across the top) with summary dashboards, variable-level analysis, collector-level breakdowns, and a detailed issue log.',
        sections: [
          { heading: 'Sub-Tabs', text: 'Each check type has sub-views: Summary Dashboard (key metrics and trends), Variable Analysis (per-variable breakdown), Collector Analysis (per-enumerator breakdown), and Issue Log (row-level list of all flagged records).' },
          { heading: 'Time-Series Trends', text: 'If an interview date variable is set, the dashboard shows quality metrics over time — useful for detecting when problems started or whether they\'re improving week-over-week.' },
          { heading: 'Issue Severity', text: 'Issues are classified as Critical (>20% rate), Warning (5–20%), or Good (<5%). Focus on critical issues first — they have the largest impact on your data quality.' },
          { heading: 'Clicking Through', text: 'Click any metric card, variable row, or collector row to drill down into the specific flagged records. The Issue Log provides row-level details with respondent ID, collector, date, and the flagged value.' },
        ]
      }
    },
    'configure-back-checks': { title: 'Configure Back-Checks', icon: '⚖️', description: 'Set up comparison between primary and verification data.', tips: [{ icon: '🔗', text: 'Match respondent IDs between datasets' }, { icon: '📊', text: 'Select Type 1, 2, 3 variables for comparison' }], proTip: 'Type 1 = exact match required, Type 2 = numeric tolerance, Type 3 = categorical.',
      detailed: {
        overview: 'Configure how primary and back-check datasets are compared. Variables are categorized into three types with different matching rules and error weights.',
        sections: [
          { heading: 'Type 1 — Demographics (weight: 10)', text: 'Stable facts that should never change between interviews: name, age, gender, household size. Exact match required. Discrepancies here are the most serious — they suggest fabrication or interviewing the wrong respondent.' },
          { heading: 'Type 2 — Complex Questions (weight: 5)', text: 'Quantitative or complex questions where small differences are expected: income, expenditure, crop yields. You can set tolerance ranges (absolute values or percentages) per variable.' },
          { heading: 'Type 3 — Stability/Opinion (weight: 2)', text: 'Subjective or opinion-based questions that may genuinely vary between interviews: satisfaction ratings, future plans. Lower weight because some variation is natural.' },
          { heading: 'Tolerance Ranges', text: 'For Type 2 variables, define acceptable difference thresholds. Example: if income tolerance is set to 10%, a primary value of 1000 and backcheck value of 1050 (5% difference) would pass, but 1200 (20%) would fail.' },
          { heading: 'ID Matching', text: 'Select the respondent ID field in both datasets. DataQuad merges on this field to pair up primary and verification records for comparison.' },
        ]
      }
    },
    'backcheckdashboard': { title: 'Back-Check Results', icon: '📋', description: 'Review discrepancies between primary and back-check data.', tips: [{ icon: '📊', text: 'View match rates by variable type' }, { icon: '👤', text: 'Analyze discrepancies by collector' }], proTip: 'High discrepancy rates for one collector may indicate data quality issues.',
      detailed: {
        overview: 'Displays the results of back-check analysis with match rates, weighted error scores, enumerator comparisons, temporal patterns, and systematic bias detection.',
        sections: [
          { heading: 'Overall Summary', text: 'Shows total records matched, overall match rate, and weighted quality score. The weighted score accounts for error severity — a Type 1 mismatch (weight 10) counts much more than Type 3 (weight 2).' },
          { heading: 'Enumerator Analysis', text: 'Per-collector breakdown showing each enumerator\'s match rates, back-check coverage (what % of their surveys were re-verified), and weighted error scores. Enumerators with consistently low match rates need attention.' },
          { heading: 'Systematic Bias', text: 'DataQuad statistically tests whether discrepancies follow a pattern (e.g., a collector always reports higher income than the back-check finds). Uses confidence intervals and statistical tests to distinguish random error from systematic bias.' },
          { heading: 'Discrepancy Patterns', text: 'Clusters of related errors — for example, if the same respondent has mismatches across multiple Type 1 variables, that\'s a stronger signal of fabrication than isolated mismatches.' },
        ]
      }
    },
    'cleaning-studio': { title: 'Cleaning Studio', icon: '🧹', description: 'Create and apply data cleaning transformations.', tips: [{ icon: '📝', text: 'Write cleaning scripts or use visual editor' }, { icon: '👁️', text: 'Preview changes before applying' }], proTip: 'Always preview cleaning operations on a sample before full application.',
      detailed: {
        overview: 'A Python-based data cleaning editor powered by Monaco (the VS Code editor). Write cleaning scripts, preview changes with dry runs, maintain version history, and execute against your dataset — all without leaving DataQuad.',
        sections: [
          { heading: 'Editor', text: 'Full-featured Python editor with syntax highlighting, autocomplete, and error detection. Your dataset is available as a pandas DataFrame called "df". Write standard pandas operations to transform, clean, or filter your data.' },
          { heading: 'Dry Run / Preview', text: 'Before applying changes, run a preview to see exactly which rows and columns will be affected, what values will change, and summary statistics of the impact. This is non-destructive — your data is safe until you execute.' },
          { heading: 'Version History', text: 'Every saved script is versioned. You can browse, compare, and restore previous versions. This gives you an audit trail of all cleaning operations applied to the dataset.' },
          { heading: 'Verification Queue', text: 'Issues flagged during quality checks can be sent to a verification queue. Generate WhatsApp messages for field teams to verify specific records. Track verification status and field feedback directly in the studio.' },
          { heading: 'AI Assistance', text: 'DataQuad can suggest cleaning scripts based on detected issues. The AI generates pandas code to address specific problems (e.g., fixing outlier values, standardizing text, filling missing data).' },
        ]
      }
    },
    'cleaned-review': { title: 'Comparison Dashboard', icon: '⚖️', description: 'Compare original vs cleaned data quality metrics.', tips: [{ icon: '📊', text: 'Side-by-side comparison of quality scores' }, { icon: '📈', text: 'Track improvement in each metric' }], proTip: 'Quality scores should improve after cleaning. If not, review your cleaning rules.',
      detailed: {
        overview: 'Side-by-side comparison of raw vs cleaned data quality metrics. Shows the impact of your cleaning operations with before/after visualizations and improvement percentages.',
        sections: [
          { heading: 'What It Compares', text: 'All quality check results that you\'ve run on both raw and cleaned data: uniqueness rates, completeness percentages, outlier counts, duration distributions, and more. Each metric shows the delta (improvement or regression).' },
          { heading: 'How to Use', text: 'Run quality checks on raw data first (Configure Detectors → Detective Dashboard). Then clean your data (Cleaning Studio). Then re-run the same checks. The Comparison Dashboard automatically pulls both sets of results.' },
          { heading: 'Validation', text: 'If a metric got worse after cleaning, something went wrong — review your cleaning script. Good cleaning should reduce duplicates, lower missing rates, bring outliers within range, and improve duration distributions.' },
        ]
      }
    },
    'data-export': { title: 'Data Export', icon: '📤', description: 'Export your data in various formats.', tips: [{ icon: '📁', text: 'Export as CSV, Excel, or Stata format' }, { icon: '🧹', text: 'Choose original or cleaned dataset' }], proTip: 'Include flag columns to track which records were modified during cleaning.',
      detailed: {
        overview: 'Export your raw or cleaned dataset in your preferred format. Flag columns can be included to track which records were modified, flagged, or cleaned during your quality assessment.',
        sections: [
          { heading: 'Formats', text: 'CSV (.csv), Excel (.xlsx), and Stata (.dta). Choose the format that best fits your downstream workflow — Stata for statistical analysis, Excel for sharing, CSV for universal compatibility.' },
          { heading: 'What to Export', text: 'Choose between original (raw) data and cleaned data. You can also include quality flag columns that mark which records had issues and what type (duplicate, outlier, missing, etc.).' },
        ]
      }
    },
    'generate-report': { title: 'Generate Report', icon: '📄', description: 'Create comprehensive quality assessment reports.', tips: [{ icon: '📊', text: 'Select metrics to include in report' }, { icon: '📈', text: 'Add visualizations and charts' }], proTip: 'Reports are great for sharing findings with stakeholders and documenting your process.',
      detailed: {
        overview: 'Generate a polished data quality assessment report summarizing all your checks, findings, and cleaning operations. Ideal for sharing with PIs, donors, or project managers.',
        sections: [
          { heading: 'Report Contents', text: 'Executive summary with overall quality score, per-check breakdowns with visualizations, collector performance rankings, time-series quality trends, cleaning operations log, and recommendations.' },
          { heading: 'Customization', text: 'Select which checks and metrics to include. Add your project name, organization, and notes. Choose between summary and detailed report levels.' },
        ]
      }
    },
  };
 
  const content = documentationContent[modalId] || {
    title: 'Welcome to DataQuad', icon: '👋', description: 'Your intelligent survey data quality assistant.',
    tips: [{ icon: '1️⃣', text: 'Start by creating or selecting a project' }, { icon: '2️⃣', text: 'Load your survey data in Data Loader' }],
    proTip: 'Follow the pipeline from top to bottom for a complete quality assessment.',
    detailed: {
      overview: 'DataQuad guides you through a complete data quality assessment pipeline: load your data, explore it, run automated checks, clean issues, validate improvements, and export results.',
      sections: [
        { heading: 'The Pipeline', text: 'SETUP (manage projects) → LOAD (import data and forms) → EXPLORE (preview, summarize, correlate) → ANALYSE (configure and run quality checks, back-checks, AI assessment) → CLEAN (write and execute cleaning scripts) → ASSURE (compare raw vs cleaned) → EXPORT (download data and reports).' },
        { heading: 'Getting Started', text: 'Create a project, upload your primary survey data, optionally load an XLSForm for smarter checks, then head to Configure Detectors to set up your quality checks.' },
      ]
    }
  };
 
  return (
    <div style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <p style={{
        color: isDarkMode ? '#D1D5DB' : '#4B5563',
        marginBottom: '16px',
        lineHeight: '1.6',
        fontSize: '13px',
        fontWeight: '400'
      }}>
        {isDetailed && content.detailed ? content.detailed.overview : content.description}
      </p>
      
      {/* Compact: tips */}
      {!isDetailed && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: '20px',
        }}>
          {content.tips.map((tip, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '10px 12px',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: '10px',
            }}>
              <span style={{
                width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0, marginTop: '0px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isDarkMode ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.12)',
                color: isDarkMode ? '#60a5fa' : '#2563EB', fontSize: '10px', fontWeight: '700',
              }}>{index + 1}</span>
              <span style={{ color: colors.text, fontSize: '12px', lineHeight: '1.5' }}>{tip.text}</span>
            </div>
          ))}
        </div>
      )}
 
      {/* Detailed: sections */}
      {isDetailed && content.detailed && content.detailed.sections && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginBottom: '20px',
        }}>
          {content.detailed.sections.map((section, index) => (
            <div key={index} style={{
              padding: '14px 16px',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: '12px',
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
            }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '11px',
                fontWeight: '600',
                color: isDarkMode ? '#93C5FD' : '#2563EB',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '6px',
              }}>
                <span style={{
                  width: '5px', height: '5px', borderRadius: '50%',
                  backgroundColor: isDarkMode ? '#93C5FD' : '#2563EB',
                }} />
                {section.heading}
              </div>
              <p style={{
                color: colors.text,
                fontSize: '12px',
                lineHeight: '1.6',
                margin: 0,
              }}>{section.text}</p>
            </div>
          ))}
        </div>
      )}
      
      {content.proTip && (
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '14px 16px',
          background: isDarkMode ? '#0A0A0B' : '#FAFAFA',
          borderRadius: '12px',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
        }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isDarkMode ? 'rgba(217,119,6,0.16)' : 'rgba(217,119,6,0.1)',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6"/><path d="M10 22h4"/>
              <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
            </svg>
          </div>
          <div>
            <div style={{
              fontSize: '10px', fontWeight: '700', color: '#D97706',
              textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px'
            }}>Pro Tip</div>
            <p style={{ color: colors.text, lineHeight: '1.4', fontSize: '12px', margin: 0 }}>{content.proTip}</p>
          </div>
        </div>
      )}
    </div>
  );
};
 
 
// 5. MOBILE BOTTOM SHEET COMPONENT
 
 
const MobileBottomSheet = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  isDarkMode, 
  colors,
  maxHeight = '70vh'
}) => {
  const [dragY, setDragY] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const startYRef = React.useRef(0);
  const contentRef = React.useRef(null);
  const DISMISS_THRESHOLD = 120;
 
  React.useEffect(() => {
    if (isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);
 
  // Scroll the currently-active menu item into the centre of the sheet's
  // scrollable content area when the sheet opens. Operates only on the
  // sheet's own scroll container — never scrolls the page behind.
  React.useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const container = contentRef.current;
      if (!container) return;
      const activeEl = container.querySelector('[data-active-menu-item="true"]');
      if (!activeEl) return;
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      const offset =
        (activeRect.top - containerRect.top) +
        container.scrollTop -
        (container.clientHeight - activeEl.clientHeight) / 2;
      container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
    }, 340); // wait out the slideUp animation (300ms) + small buffer
    return () => clearTimeout(t);
  }, [isOpen]);
 
  const handleTouchStart = React.useCallback((e) => {
    startYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);
 
  const handleTouchMove = React.useCallback((e) => {
    if (!isDragging) return;
    const deltaY = e.touches[0].clientY - startYRef.current;
    setDragY(Math.max(0, deltaY));
  }, [isDragging]);
 
  const handleTouchEnd = React.useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > DISMISS_THRESHOLD) {
      setDragY(window.innerHeight);
      setTimeout(() => {
        onClose();
        setDragY(0);
      }, 200);
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, onClose]);
 
  if (!isOpen) return null;
 
  const backdropOpacity = Math.max(0, 0.5 - (dragY / (DISMISS_THRESHOLD * 4)));
 
  return (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: `rgba(0,0,0,${backdropOpacity})`,
          zIndex: 998,
          animation: dragY === 0 ? 'fadeIn 0.2s ease-out' : undefined
        }}
      />
      
      {/* Sheet */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: maxHeight,
        backgroundColor: isDarkMode ? 'rgba(13, 14, 14, 0.92)' : 'rgba(248, 245, 238, 0.94)',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        zIndex: 999,
        animation: dragY === 0 && !isDragging ? 'slideUp 0.3s ease-out' : undefined,
        transform: `translateY(${dragY}px)`,
        transition: isDragging ? 'none' : 'transform 0.25s ease-out',
        backdropFilter: 'blur(24px) saturate(140%)',
        WebkitBackdropFilter: 'blur(24px) saturate(140%)',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        borderBottom: 'none',
        boxShadow: isDarkMode ? '0 -12px 40px rgba(0, 0, 0, 0.5)' : '0 -12px 40px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        {/* Drag Handle + Header */}
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none', cursor: 'grab' }}
        >
          {/* Handle bar with bounce */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '10px',
          }}>
            <div
              className="dq-sheet-handle"
              style={{
                width: '36px',
                height: '3px',
                backgroundColor: isDragging
                  ? (isDarkMode ? '#a8a49a' : '#555550')
                  : (isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'),
                borderRadius: '100px',
                transition: 'background-color 0.15s ease',
              }}
            />
          </div>
 
          {/* Header */}
          {title && (
            <div style={{
              padding: '2px 20px 14px',
              borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: 500,
                  letterSpacing: '0.16em',
                  textTransform: 'lowercase',
                  color: colors.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <span style={{
                    width: '5px', height: '5px', borderRadius: '50%',
                    background: '#2563EB',
                    boxShadow: '0 0 6px rgba(37, 99, 235, 0.5)',
                    animation: 'dlBreathe 2.4s ease-in-out infinite',
                  }} />
                  menu
                </div>
                <h3 style={{
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  color: colors.text
                }}>
                  {title}
                </h3>
              </div>
            </div>
          )}
        </div>
 
        {/* Content */}
        <div
          ref={contentRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px 20px 20px',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {children}
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes handleBounce {
          0%, 100% { transform: translateY(0); }
          40% { transform: translateY(6px); }
          60% { transform: translateY(3px); }
          80% { transform: translateY(5px); }
        }
        @keyframes dlBreathe {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.4); opacity: 0.55; }
        }
        .dq-sheet-handle {
          animation: handleBounce 0.6s ease-out 0.35s 1;
        }
      `}</style>
    </>
  );
};
 
 
// 6. MAIN COMPONENT
 
 
const PipelineNavigation = ({
  onOpenModal,
  onSelectModal = null,
  onProjectSelect,
  activeModal,
  projectLoaded = false,
  projectName = null,
  dataLoaded = false,
  qualityChecksRun = false,
  cleaningComplete = false,
  checkResults = null,
  backcheckDataLoaded = false,
  hasAudio = false,
  userName,
  userPicture,
  onLogout,
  previewContent = null,
  previewModalId = null,
  onPreviewClick = null,
}) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const settingsNavigate = useNavigate();
  const { isMobile, isTablet, isDesktop, width } = useResponsive();
  const [hoveredNode, setHoveredNode] = useState(null);
  const [lastVisitedModal, setLastVisitedModal] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [guideCollapsed, setGuideCollapsed] = useState(() => {
    try { return localStorage.getItem('dq_guide_collapsed') === 'true'; } catch { return false; }
  });
  const [guideDetailed, setGuideDetailed] = useState(() => {
    try { return localStorage.getItem('dq_guide_detailed') === 'true'; } catch { return false; }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('dq_sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem('dq_collapsed_sections');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
 
  // Auto-scale refs/scales for each layout's preview frame.
  // The hook is cheap (noop when the ref element isn't mounted), so it's
  // safe to call all three unconditionally — keeps hook order stable.
  const [desktopPreviewRef, desktopPreviewScale] = useAutoScale(1600, 0.45, 2.0);
  const [tabletPreviewRef,  tabletPreviewScale ] = useAutoScale(1400, 0.45, 2.0);
  const [mobilePreviewRef,  mobilePreviewScale ] = useAutoScale(900,  0.38, 1.6);
 
  // Load Outfit font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector('link[href*="Outfit"]')) {
      document.head.appendChild(link);
    }
  }, []);
 
  const toggleGuide = useCallback(() => {
    setGuideCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('dq_guide_collapsed', String(next)); } catch {}
      return next;
    });
  }, []);
 
  const toggleGuideDetail = useCallback(() => {
    setGuideDetailed(prev => {
      const next = !prev;
      try { localStorage.setItem('dq_guide_detailed', String(next)); } catch {}
      return next;
    });
  }, []);
 
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('dq_sidebar_collapsed', String(next)); } catch {}
      return next;
    });
  }, []);
 
  const toggleSection = useCallback((sectionId) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      try { localStorage.setItem('dq_collapsed_sections', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  
  useEffect(() => {
    if (activeModal) setLastVisitedModal(activeModal);
  }, [activeModal]);
  
  const colors = {
    bg: isDarkMode ? '#0A0A0B' : '#FAFAFA',
    nodeBg: isDarkMode ? 'rgba(20, 20, 25, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    nodeHover: isDarkMode ? 'rgba(40, 40, 45, 0.8)' : 'rgba(250, 250, 250, 0.9)',
    nodeBorder: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    text: isDarkMode ? '#7e7e7e' : '#111827',
    textSecondary: isDarkMode ? '#7e7e7e' : '#6B7280',
    textMuted: isDarkMode ? '#4B5563' : '#9CA3AF',
    textDisabled: isDarkMode ? '#27272a' : '#E5E7EB',
    accent: '#4FC3F7', // '#38B6FF',
    gold: '#D4A574',
  };
 
  const pipeline = [
    { id: 'setup', label: 'Setup', enabled: true, icon: StageIcons.setup, children: [{ id: 'projects', label: 'Projects', icon: FolderOpen }] },
    { id: 'data-loader', label: 'Load', enabled: projectLoaded, icon: StageIcons.dataLoader, children: [{ id: 'primary-survey', label: 'Primary Survey', icon: Database }, { id: 'xlsform', label: 'XLSForm', icon: FileSpreadsheet }, { id: 'backcheck-dataset', label: 'Back-Check Dataset', icon: ClipboardCheck }] },
    { id: 'explore', label: 'Explore', enabled: dataLoaded, icon: StageIcons.explore, children: [
      { id: 'datepreview',         label: 'Data Preview',        icon: TableIcon },
      { id: 'data-summary',        label: 'Data Summary',        icon: ListChecks },
      { id: 'correlation-matrix',  label: 'Correlation Matrix',  icon: GitCompare },
      { id: 'collector-dashboard', label: 'Collector Dashboard', icon: Users },
      // Audio Audit hidden until primary data containing audio file columns
      // is loaded — same pattern as back-check tools (line below).
      ...(hasAudio ? [{ id: 'audio-audit', label: 'Audio Audit', icon: Headphones }] : []),
    ] },
    { id: 'quality-analysis', label: 'Analyse', enabled: dataLoaded, icon: StageIcons.qualityAnalysis, children: [
      { id: 'quality-checks', label: 'Configure Detectors', icon: Settings },
      { id: 'dashboard', label: 'Detective Dashboard', icon: Layout },
      // Back-check tools hidden entirely until verification data is loaded —
      // showing them disabled is noise; the user can't act on them and they
      // crowd out the AI/Detective tools that ARE actionable.
      ...(backcheckDataLoaded ? [
        { id: 'configure-back-checks', label: 'Configure Backchecks', icon: Settings },
        { id: 'backcheckdashboard', label: 'Back-checks', icon: Layout },
      ] : []),
    ] },
    { id: 'data-cleaning', label: 'Clean', enabled: qualityChecksRun, icon: StageIcons.dataCleaning, children: [{ id: 'cleaning-studio', label: 'Cleaning Studio', icon: Zap }] },
    { id: 'cleaned-data', label: 'Assure', enabled: cleaningComplete, icon: StageIcons.cleanedData, children: [{ id: 'cleaned-review', label: 'Comparison Dashboard', icon: CheckCircle2 }] },
    { id: 'exports-reports', label: 'Export', enabled: cleaningComplete, icon: StageIcons.exportsReports, children: [{ id: 'data-export', label: 'Data Export', icon: Download }, { id: 'generate-report', label: 'Generate Report', icon: FileText }] },
  ];
  
  // Resolve preview/selection target — prop from App.js wins, local state is fallback.
  // Defined BEFORE isLastVisited so the menu highlight stays in sync with the
  // canonical lastVisitedModal in App.js (updated by handleSelectModal on every
  // sidebar click). Without this, the local lastVisitedModal only updates when
  // activeModal changes, so the highlight gets stuck on the last item that was
  // actually opened in the modal.
  const displayPreviewModalId = previewModalId || lastVisitedModal;
  const showPreview = !activeModal && displayPreviewModalId && previewContent;
 
  const isNodeActive = (nodeId, targetModal) => activeModal === (targetModal || nodeId);
  const isLastVisited = (nodeId, targetModal) => !activeModal && displayPreviewModalId === (targetModal || nodeId);
  const handleNodeClick = (nodeId, targetModal) => {
    const id = targetModal || nodeId;
    if (activeModal) {
      // Modal already open → swap content in place so navigation stays
      // smooth while the user is inside the workspace.
      onOpenModal(id);
    } else {
      // Modal closed → just light up the menu item + update the
      // workspace preview. User explicitly opens via "resume work".
      (onSelectModal || onOpenModal)(id);
    }
    if (isMobile) setMobileMenuOpen(false);
  };
 
  const modalLabels = {
    'projects': 'Projects', 'primary-survey': 'Primary Survey', 'xlsform': 'XLSForm', 'backcheck-dataset': 'Back-Check Dataset',
    'datepreview': 'Data Preview', 'data-summary': 'Data Summary', 'correlation-matrix': 'Correlation Matrix', 'collector-dashboard': 'Collector Dashboard',
    'audio-audit': 'Audio Audit',
    'quality-checks': 'Configure Detectors', 'dashboard': 'Detective Dashboard', 'configure-back-checks': 'Configure Backchecks',
    'backcheckdashboard': 'Back-checks', 'cleaning-studio': 'Cleaning Studio',
    'cleaned-review': 'Comparison Dashboard', 'data-export': 'Data Export', 'generate-report': 'Generate Report',
  };
  
  
  // Render child item
  const renderChildItem = (child, parentEnabled, stageColor, index) => {
    const isEnabled = parentEnabled;
    const isActive = isNodeActive(child.id, child.targetModal);
    const wasLastVisited = isLastVisited(child.id, child.targetModal);
    const isHovered = hoveredNode === child.id;
    const isChildHighlighted = isActive || wasLastVisited;
    const collapsed = !isMobile && sidebarCollapsed;
    
    return (
      <div key={child.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '2px', position: 'relative' }}>
        {/* Horizontal Link Line - mobile only */}
        {isMobile && (
          <div style={{
            width: '12px', 
            height: '1.5px',
            background: isChildHighlighted ? `linear-gradient(90deg, ${stageColor}40, ${stageColor})` : (isDarkMode ? '#374151' : '#E5E7EB'),
            marginRight: '8px', 
            opacity: isEnabled ? 1 : 0.3
          }} />
        )}
        
        <button
          data-active-menu-item={isActive ? 'true' : undefined}
          onClick={() => isEnabled && handleNodeClick(child.id, child.targetModal)}
          onMouseEnter={() => !isMobile && setHoveredNode(child.id)}
          onMouseLeave={() => !isMobile && setHoveredNode(null)}
          disabled={!isEnabled}
          title={collapsed ? child.label : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '0',
            padding: isMobile ? '8px 12px' : collapsed ? '6px 0' : '6px 8px 6px 4px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            backgroundColor: isChildHighlighted
              ? (isDarkMode ? '#9b9b9b' : '#0D0E0E')    // menu item background color
              : (isHovered && isEnabled)
                ? (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')
                : 'transparent',
            border: '0.5px solid transparent',
            borderRadius: '7px',
            cursor: isEnabled ? 'pointer' : 'not-allowed',
            opacity: isEnabled ? 1 : 0.5,
            transition: 'all 0.18s ease',
            color: isChildHighlighted
              ? (isDarkMode ? '#0D0E0E' : '#F8F5EE')    // menu item text color
              : isEnabled ? colors.textSecondary : colors.textDisabled,
            width: '100%',
            textAlign: 'left',
            minHeight: isMobile ? '44px' : 'auto',
            fontFamily: "'Outfit', -apple-system, sans-serif",
            letterSpacing: '-0.005em',
          }}
        >
          {/* Fixed-width icon column matching parent icon width for alignment */}
          {child.icon && !isMobile && !collapsed && (
            <div style={{ width: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: isChildHighlighted ? 1 : 0.65 }}>
              <child.icon size={14} strokeWidth={1.75} />
            </div>
          )}
          {child.icon && (isMobile || collapsed) && (
            <child.icon size={isMobile ? 18 : 14} strokeWidth={1.75} style={{ flexShrink: 0, opacity: isChildHighlighted ? 1 : 0.75 }} />
          )}
          {!collapsed && (
            <span style={{
              fontSize: isMobile ? '14px' : '12.5px',
              fontWeight: isChildHighlighted ? 500 : 400,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginLeft: isMobile ? '0' : '2px',
              letterSpacing: '-0.005em',
            }}>
              {child.label}
            </span>
          )}
        </button>
      </div>
    );
  };
  
  // Render main pipeline node
  const renderPipelineNode = (node, index) => {
    const isEnabled = node.enabled;
    const hasActiveChild = node.children?.some(c => isNodeActive(c.id, c.targetModal) || isLastVisited(c.id, c.targetModal));
    const isHovered = hoveredNode === node.id;
    const stageColor = isEnabled ? (isHovered || hasActiveChild ? colors.accent : colors.textSecondary) : colors.textDisabled;
    const IconComponent = node.icon;
    const collapsed = !isMobile && sidebarCollapsed;
    // Section is open if: it has an active child (forced), or it's not manually collapsed
    const isSectionOpen = hasActiveChild || !collapsedSections[node.id];
    
    const handleIconClick = () => {
      if (!isEnabled || isMobile) return;
      toggleSection(node.id);
    };
    
    return (
      <div key={node.id} style={{ 
        position: 'relative', 
        display: 'flex', 
        flexDirection: collapsed ? 'column' : 'row',
        alignItems: collapsed ? 'center' : 'flex-start', 
        marginBottom: collapsed ? '0' : '8px',
        paddingRight: isMobile ? '16px' : '0'
      }}>
 
        <div
          onClick={handleIconClick}
          onMouseEnter={() => !isMobile && setHoveredNode(node.id)}
          onMouseLeave={() => !isMobile && setHoveredNode(null)}
          title={collapsed ? node.label : isSectionOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
          style={{
            zIndex: 2, 
            marginRight: isMobile ? '12px' : collapsed ? '0' : '8px',
            filter: isEnabled ? 'none' : 'grayscale(100%) opacity(0.4)',
            transition: 'transform 0.2s ease',
            transform: isHovered && isEnabled && !isMobile ? 'scale(1.06)' : 'scale(1)',
            cursor: isEnabled ? 'pointer' : 'default',
            flexShrink: 0
          }}
        >
          {IconComponent && <IconComponent 
            color={stageColor} 
            size={isMobile ? 30 : 28} 
            isActive={hasActiveChild} 
            isHovered={isHovered} 
          />}
        </div>
        
        <div style={{ 
          flex: collapsed ? undefined : 1, 
          paddingBottom: collapsed ? '8px' : (isSectionOpen ? '14px' : '8px'), 
          minWidth: 0,
          width: collapsed ? '100%' : undefined,
          display: 'flex',
          flexDirection: 'column',
          alignItems: collapsed ? 'center' : 'stretch',
          transition: 'padding-bottom 0.25s ease',
        }}>
          {!collapsed && (
            <div 
              onClick={handleIconClick}
              style={{
                fontSize: isMobile ? '12px' : '10px',
                fontWeight: 500,
                color: hasActiveChild ? (isDarkMode ? '#f0ede4' : '#1a1a1a') : isEnabled ? colors.textMuted : colors.textDisabled,
                marginBottom: isSectionOpen ? '10px' : '0',
                marginTop: isMobile ? '8px' : '8px',
                letterSpacing: '0.16em',
                textTransform: 'lowercase',
                cursor: isEnabled ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                userSelect: 'none',
                opacity: hasActiveChild ? 1 : 0.7,
                transition: 'opacity 0.2s ease',
              }}>
              <span>{typeof node.label === 'string' ? node.label.toLowerCase() : node.label}</span>
              {isEnabled && !isMobile && (
                <ChevronDown size={9} style={{
                  transition: 'transform 0.25s ease',
                  transform: isSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  opacity: 0.4,
                }} />
              )}
            </div>
          )}
          
          {node.children && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              marginLeft: isMobile ? '0' : collapsed ? '0' : '-30px',
              marginTop: isMobile ? '0' : collapsed ? '0' : '2px',
              alignItems: collapsed ? 'center' : 'stretch',
              maxHeight: (isMobile || isSectionOpen) ? '500px' : '0',
              overflow: 'hidden',
              opacity: (isMobile || isSectionOpen) ? 1 : 0,
              transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease',
            }}>
               {/* Vertical Branch Line - mobile only */}
               {node.children.length > 0 && isMobile && (
                <div style={{
                  position: 'absolute', left: '23px', top: '50px', bottom: '20px',
                  width: '2px', backgroundColor: isEnabled ? (isDarkMode ? '#374151' : '#E5E7EB') : 'transparent',
                  zIndex: 0
                }} />
              )}
              {node.children.map((child, idx) => renderChildItem(child, isEnabled, stageColor, idx))}
            </div>
          )}
        </div>
      </div>
    );
  };
 
  
  // MOBILE LAYOUT
  
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        backgroundColor: colors.bg, 
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1,
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        overflow: 'hidden'
      }}>
        <AuroraBackground 
          blobCount={3} 
          animationSpeed={0.2} 
          blurAmount={100}
          saturation={isDarkMode ? 70 : 60} 
          baseOpacity={isDarkMode ? 0.2 : 0.3}
        />
        
        {/* Mobile Header */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'calc(16px + env(safe-area-inset-top, 0px)) 20px 14px',
          backgroundColor: isDarkMode ? 'rgba(13, 14, 14, 0.85)' : 'rgba(248, 245, 238, 0.88)',
          backdropFilter: 'blur(20px) saturate(140%)',
          WebkitBackdropFilter: 'blur(20px) saturate(140%)',
          borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src={isDarkMode ? logoSourceInverted : logoSource}
              alt="DataQuad"
              style={{ width: '28px', height: '28px', objectFit: 'contain' }}
            />
            <span style={{
              fontSize: '15px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: colors.text,
            }}>
              DataQuad
            </span>
          </div>
 
          {/* Project Name Badge */}
          {projectLoaded && projectName && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 10px',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              borderRadius: '100px',
              maxWidth: '50%',
              overflow: 'hidden',
            }}>
              <FolderOpen size={12} style={{ flexShrink: 0, color: colors.textMuted, opacity: 0.75 }} />
              <span style={{
                fontSize: '11.5px',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                color: colors.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {projectName}
              </span>
            </div>
          )}
        </div>
        
        {/* Mobile Main Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px 8px 20px',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          zIndex: 5
        }}>
          {/* Quick Actions */}
          {showPreview && (
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '18px'
            }}>
              <button
                onClick={() => setMobileGuideOpen(true)}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  borderRadius: '7px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  color: colors.text,
                  fontFamily: "'Outfit', -apple-system, sans-serif",
                  letterSpacing: '-0.005em',
                }}
              >
                <FileText size={15} style={{ opacity: 0.7 }} />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>quick guide</span>
              </button>
 
              <button
                onClick={() => onPreviewClick && onPreviewClick(displayPreviewModalId)}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  background: isDarkMode ? '#797979' : '#252525d0',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  color: isDarkMode ? '#000000' : '#F8F5EE',
                  fontFamily: "'Outfit', -apple-system, sans-serif",
                  letterSpacing: '-0.005em',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Zap size={15} />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>resume</span>
              </button>
            </div>
          )}
          
          {/* Preview Card (clickable) */}
          {showPreview && (
            <div 
              className="gradient-border-wrapper active"
              style={{
                // Inline fallback — see desktop counterpart below for rationale.
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                padding: '3px',
                marginBottom: '20px',
                borderRadius: '18px',
              }}
            >
            <div
              onClick={() => onPreviewClick && onPreviewClick(displayPreviewModalId)}
              className="gradient-border-inner"
              style={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                height: 'calc(100vh - 220px)',
                minHeight: '500px',
                maxHeight: 'none',
                cursor: 'pointer',
                backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)'
              }}
            >
              <div ref={mobilePreviewRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '900px',  /* fixed logical width — mobile dashboard layout stays constant */
                  /* Explicit logical height = visible window / scale. A transform
                     ancestor becomes the containing block for position:fixed, so
                     fixed overlays (loaders) size to THIS box — matching it to the
                     visible crop keeps them fully in view instead of truncating. */
                  height: mobilePreviewScale ? `${100 / mobilePreviewScale}%` : '100%',
                  overflow: 'hidden',
                  transform: `scale(${mobilePreviewScale})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                  <div className="preview-transparent-mode" style={{ pointerEvents: 'none' }}>
                    {previewContent}
                  </div>
                </div>
              </div>
              
              {/* Label */}
              <div style={{
                position: 'absolute',
                top: '0px',
                left: '0px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: colors.nodeBg,
                backdropFilter: 'blur(10px)',
                borderRadius: '0 0 8px 0',
                border: `1px solid ${colors.nodeBorder}`,
                borderTop: 'none',
                borderLeft: 'none',
                zIndex: 3
              }}>
                <span style={{ 
                  width: '6px', height: '6px', borderRadius: '50%', 
                  backgroundColor: colors.accent, 
                  boxShadow: `0 0 8px ${colors.accent}` 
                }} />
                <span style={{ fontSize: '12px', fontWeight: '500', color: colors.text }}>
                  {modalLabels[displayPreviewModalId] || 'Workspace'}
                </span>
              </div>
            </div>
            </div>
          )}
          
          {/* Welcome Landing - shown when no preview */}
          {!showPreview && (
            <WelcomeLanding
              compact={true}
              userName={userName}
              userPicture={userPicture}
              projectLoaded={projectLoaded}
              projectName={projectName}
              dataLoaded={dataLoaded}
              qualityChecksRun={qualityChecksRun}
              cleaningComplete={cleaningComplete}
              onOpenModal={onOpenModal}
              onProjectSelect={onProjectSelect}
            />
          )}
          
          {/* Mini Pipeline Overview - only shown after project selected */}
          {projectLoaded && (
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '2px',
            padding: '14px 16px',
            border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
            <div style={{
              fontSize: '10px',
              fontWeight: 500,
              color: colors.textMuted,
              textTransform: 'lowercase',
              letterSpacing: '0.16em',
              marginBottom: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                background: '#2563EB',
                boxShadow: '0 0 6px rgba(37, 99, 235, 0.5)',
                animation: 'dlBreathe 2.4s ease-in-out infinite',
              }} />
              pipeline progress
            </div>
 
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
              {pipeline.map((node, idx) => {
                const nextEnabled = idx < pipeline.length - 1 && pipeline[idx + 1].enabled;
                return (
                  <React.Fragment key={node.id}>
                    <div style={{
                      padding: '5px 11px',
                      borderRadius: '100px',
                      backgroundColor: node.enabled
                        ? (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')
                        : 'transparent',
                      border: `0.5px solid ${node.enabled
                        ? (isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)')
                        : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')}`,
                      opacity: node.enabled ? 1 : 0.4,
                      transition: 'all 0.2s ease',
                    }}>
                      <span style={{
                        fontSize: '10.5px',
                        fontWeight: 500,
                        letterSpacing: '0.04em',
                        textTransform: 'lowercase',
                        color: node.enabled ? colors.text : colors.textDisabled,
                      }}>
                        {typeof node.label === 'string' ? node.label.toLowerCase() : node.label}
                      </span>
                    </div>
                    {idx < pipeline.length - 1 && (
                      <div style={{
                        width: '10px',
                        height: '1px',
                        backgroundColor: nextEnabled ? '#D97706' : (isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'),
                        opacity: nextEnabled ? 0.6 : 0.4,
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          )}
        </div>
        
        {/* Mobile Footer with Notch FAB */}
        <div style={{ position: 'relative', zIndex: 10, backgroundColor: colors.nodeBg, paddingBottom: 'env(safe-area-inset-bottom, 8px)' }}>
          
          {/* FAB Button */}
          <div style={{
            position: 'absolute',
            top: '-4px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 12,
          }}>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="dq-fab-btn"
              style={{
                width: '45px',
                height: '45px',
                borderRadius: '50%',
                border: `0px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.6)'}`,
                background: isDarkMode
                  ? 'linear-gradient(145deg, #111111 0%, #111111 100%)'
                  : 'linear-gradient(145deg, #111111 0%, #111111 100%)',
                color: '#FFFFFF',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1,
                boxShadow: isDarkMode
                  ? '0 4px 20px rgba(37, 99, 235, 0.38), 0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 4px 20px rgba(37, 99, 235, 0.32), 0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.15s ease',
              }}
              onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.88)'; }}
              onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <img src={logoSourceInverted} alt="DataQuad" style={{ width: '35px', height: '35px', objectFit: 'contain', filter: 'brightness(1.1)' }} />
            </button>
          </div>
 
          {/* SVG Notch */}
          <div style={{ position: 'relative', overflow: 'visible' }}>
            <svg 
              width="100%" 
              height="15" 
              viewBox="0 0 400 15" 
              preserveAspectRatio="none"
              style={{ display: 'block', position: 'relative', zIndex: 1, overflow: 'visible' }}
            >
              <defs>
                <clipPath id="notchClip">
                  <path d="M0,52 L0,0 L169,0 Q174,0 177,16 Q183,46 200,46 Q217,46 223,16 Q227,0 231,0 L400,0 L400,52 Z" />
                </clipPath>
              </defs>
              <rect width="400" height="52" fill={colors.nodeBg} clipPath="url(#notchClip)" />
              <path 
                d="M0,0.5 L169,0.5 Q174,0.5 177,16.5 Q183,46 200,46 Q217,46 223,16.5 Q227,0.5 231,0.5 L400,0.5" 
                fill="none" stroke={colors.nodeBorder} strokeWidth="1" vectorEffect="non-scaling-stroke"
              />
            </svg>
            
            {/* Footer Content */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0px 22px 8px',
              paddingRight: 'calc(10px + 52px)', // account for FAB
              // backgroundColor: colors.nodeBg,
              position: 'relative',
              zIndex: 1,
            }}>
              {/* Left: User info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0, flex: '0 1 auto' }}>
                {userName && (
                  <>
                    {userPicture && (
                      <img src={userPicture} alt="Profile" style={{ width: '26px', height: '26px', borderRadius: '2px', flexShrink: 0, objectFit: 'cover' }} />
                    )}
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                      color: colors.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: "'Outfit', -apple-system, sans-serif",
                    }}>
                      {userName}
                    </span>
                  </>
                )}
              </div>
 
              {/* Right: Settings + Theme + Logout */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button
                  onClick={() => { settingsNavigate('/settings'); }}
                  title="Account & Subscription"
                  style={{
                    width: '30px', height: '30px', borderRadius: '2px',
                    border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Settings size={13} />
                </button>
                <button
                  onClick={toggleTheme}
                  title={isDarkMode ? 'Light mode' : 'Dark mode'}
                  style={{
                    width: '30px', height: '30px', borderRadius: '2px',
                    border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isDarkMode ? <Sun size={13} /> : <Moon size={13} />}
                </button>
                <button
                  onClick={onLogout}
                  title="Log out"
                  style={{
                    width: '30px', height: '30px', borderRadius: '2px',
                    border: `0.5px solid ${isDarkMode ? 'rgba(248, 113, 113, 0.22)' : 'rgba(185, 28, 28, 0.2)'}`,
                    backgroundColor: isDarkMode ? 'rgba(248, 113, 113, 0.06)' : 'rgba(185, 28, 28, 0.04)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    color: isDarkMode ? '#f87171' : '#b91c1c',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <LogOut size={13} />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu Bottom Sheet */}
        <MobileBottomSheet
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          title="Navigation"
          isDarkMode={isDarkMode}
          colors={colors}
          maxHeight="80vh"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
            {pipeline.map((node, index) => renderPipelineNode(node, index))}
          </div>
        </MobileBottomSheet>
        
        {/* Quick Guide Bottom Sheet */}
        <MobileBottomSheet
          isOpen={mobileGuideOpen}
          onClose={() => setMobileGuideOpen(false)}
          title={`Guide: ${modalLabels[displayPreviewModalId] || 'Welcome'}`}
          isDarkMode={isDarkMode}
          colors={colors}
          maxHeight="60vh"
        >
          <ContextualDocumentation 
            modalId={displayPreviewModalId} 
            isDarkMode={isDarkMode} 
            colors={colors}
            isDetailed={guideDetailed}
          />
        </MobileBottomSheet>
        
        <style>{`
          .preview-transparent-mode > div { background: ${isDarkMode ? 'rgba(11,12,14,0.5)' : 'transparent'} !important; }
          .preview-transparent-mode .maindashboard-container > div { background: ${isDarkMode ? 'rgba(11,12,14,0.5)' : 'transparent'} !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; box-shadow: none !important; }
          .preview-transparent-mode ::-webkit-scrollbar { display: none; }
          /* The transparent-mode rules above strip backgrounds so the preview
             shows the frame canvas -- but the loading overlay NEEDS its
             backdrop, or a run-in-progress renders as floating text over the
             page (invisible in light mode, half-washed in dark). Re-assert it
             with higher specificity than both transparent rules. */
          .preview-transparent-mode .ailo-root,
          .preview-transparent-mode .maindashboard-container .ailo-root {
            background: ${isDarkMode
              ? 'radial-gradient(ellipse 80% 60% at 50% 45%, #11141A 0%, #0A0B0D 60%, #07080A 100%)'
              : 'radial-gradient(ellipse 80% 60% at 50% 45%, #FFFFFF 0%, #F2F4F7 60%, #E9ECF1 100%)'} !important;
          }
          
          @property --border-angle {
            syntax: '<angle>';
            initial-value: 0deg;
            inherits: false;
          }
          
          @keyframes borderRotate {
            to { --border-angle: 360deg; }
          }
 
          @keyframes dlBreathe {
            0%, 100% { transform: scale(1); opacity: 1; }
            50%      { transform: scale(1.4); opacity: 0.55; }
          }
          
          .gradient-border-wrapper {
            flex: 1;
            flex-direction: column;
            --border-angle: 0deg;
            position: relative;
            padding: 2px;
            border-radius: 18px;
            display: flex;
            transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease;
            will-change: transform;
          }
          
          .gradient-border-wrapper::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 18px;
            padding: 2px;
            background: ${colors.nodeBorder};
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
            z-index: 2;
          }
          
          .gradient-border-wrapper:hover {
            transform: scale(1.004);
            box-shadow: ${isDarkMode 
              ? '0 0 18px rgba(79, 195, 247, 0.28)'
              : '0 0 14px rgba(79, 195, 247, 0.18)'};
          }
          
          .gradient-border-wrapper:hover::before {
            padding: 2px;
            background: conic-gradient(
              from var(--border-angle),
              ${isDarkMode ? '#38B6FF, #C8923C, #0A0A0Bff, #0A0A0Bff, #38B6FF' : '#2196F3, #C8923C, #FAFAFAff, #FAFAFAff, #2196F3'}
            );
            animation: borderRotate 3s linear infinite;
          }
          
          .gradient-border-inner {
            background: ${isDarkMode ? '#0D0E0E' : '#F5F5F5'}
          /* CSS-only hover: no React state, so hovering never re-renders the
             (heavy, scaled) preview subtree */
          .dq-prev-body { opacity: 0.8; transition: opacity 0.3s ease; }
          .gradient-border-wrapper:hover .dq-prev-body { opacity: 1; }
          .dq-prev-shade {
            background: linear-gradient(to top, rgba(0,0,0,0.3), transparent 25%);
            transition: background 0.3s ease;
          }
          .gradient-border-wrapper:hover .dq-prev-shade {
            background: linear-gradient(to top, rgba(0,0,0,0.75), transparent 50%);
          }
          .dq-prev-hint { opacity: 0.85; transition: opacity 0.3s ease; }
          .gradient-border-wrapper:hover .dq-prev-hint { opacity: 0; }
          .dq-resume-cta {
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 0.3s ease, transform 0.3s ease;
            pointer-events: none;
          }
          .gradient-border-wrapper:hover .dq-resume-cta {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }
;
            flex: 1;
            border-radius: 16px;
            position: relative;
            overflow: hidden;
            cursor: pointer;
            z-index: 1;
          }
          
          @keyframes fabGlowShift {
            0%, 100% { box-shadow: 0 4px 20px rgba(37, 99, 235, 0.42), 0 2px 8px rgba(0,0,0,0.15); }
            50% { box-shadow: 0 4px 24px rgba(217, 119, 6, 0.38), 0 2px 8px rgba(0,0,0,0.15); }
          }
          @keyframes fabRingPulse {
            0%, 100% { transform: scale(1); opacity: 0.4; }
            50% { transform: scale(1.25); opacity: 0; }
          }
          .dq-fab-btn { animation: fabGlowShift 5s ease-in-out infinite; }
          .dq-fab-btn::after {
            content: '';
            position: absolute;
            inset: -4px;
            border-radius: 50%;
            border: 1.5px solid rgba(217, 119, 6, 0.28);
            animation: fabRingPulse 3s ease-out infinite;
            pointer-events: none;
          }
          .dq-fab-btn:active { transform: scale(0.88) !important; animation: none; }
        `}</style>
      </div>
    );
  }
 
  
  // TABLET LAYOUT (simplified desktop)
  
  if (isTablet) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: colors.bg, display: 'flex', zIndex: 1,
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        overflow: 'hidden'
      }}>
        <AuroraBackground 
          blobCount={4} animationSpeed={0.3} blurAmount={120}
          saturation={isDarkMode ? 70 : 60} baseOpacity={isDarkMode ? 0.3 : 0.4}
        />
        
        {/* Project Name - Centered Top */}
        {projectLoaded && projectName && (
          /* Quiet centred wordmark instead of a boxed pill: eyebrow + name +
             a hairline gradient tick. Reads as a title, not a button. */
          <div style={{
            position: 'fixed',
            top: '14px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '3px',
            maxWidth: '300px',
            pointerEvents: 'none',
          }}>
            <span style={{
              fontSize: '9px',
              fontWeight: 500,
              letterSpacing: '0.22em',
              textTransform: 'lowercase',
              color: colors.textMuted,
              fontFamily: "'Outfit', -apple-system, sans-serif",
            }}>
              project
            </span>
            <span style={{ 
              fontSize: '13.5px', 
              fontWeight: '500', 
              letterSpacing: '-0.01em',
              color: colors.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              fontFamily: "'Outfit', -apple-system, sans-serif",
            }}>
              {projectName}
            </span>
            <span aria-hidden="true" style={{
              width: '26px',
              height: '2px',
              borderRadius: '1px',
              background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`,
              opacity: 0.7,
            }} />
          </div>
        )}
 
        {/* CENTER - Pipeline (hidden until project selected) */}
        {projectLoaded && (
        <div className="pipeline-center-scroll" style={{ 
          flex: '0 0 auto', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: sidebarCollapsed ? 'center' : 'flex-start', 
          padding: sidebarCollapsed ? '52px 6px 40px 6px' : '52px 8px 40px 16px', 
          overflowY: 'auto', overflowX: 'hidden',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: sidebarCollapsed ? '52px' : '200px',
          minWidth: sidebarCollapsed ? '52px' : '180px',
          position: 'relative',
        }}>
          {/* Collapse/Expand Toggle */}
          <div
            role="button"
            tabIndex={0}
            onClick={toggleSidebar}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSidebar(); } }}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              position: 'absolute',
              top: '52px',
              right: sidebarCollapsed ? '50%' : '4px',
              transform: sidebarCollapsed ? 'translateX(50%)' : 'none',
              width: '22px', height: '22px', borderRadius: '6px',
              border: `1px solid ${colors.nodeBorder}`,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              color: colors.textMuted,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10,
              transition: 'all 0.2s ease',
              opacity: 0.6,
              boxSizing: 'border-box',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
          >
            {sidebarCollapsed ? <ChevronsRight size={12} /> : <ChevronsLeft size={12} />}
          </div>
 
          {/* Pipeline List */}
          <div style={{ 
            display: 'flex', flexDirection: 'column', gap: '0px', paddingBottom: '80px', paddingTop: '20px',
            width: '100%',
            alignItems: sidebarCollapsed ? 'center' : 'stretch',
          }}>
            {pipeline.map((node, index) => renderPipelineNode(node, index))}
          </div>
        </div>
        )}
        
        {/* RIGHT PANEL - Preview (larger on tablet) */}
        {showPreview && (
          <div style={{ flex: '1', padding: '60px 24px 40px 0', zIndex: 5, display: 'flex', flexDirection: 'column' }}>
             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '30%', backgroundColor: colors.accent, boxShadow: `0 0 10px ${colors.accent}` }} />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: colors.textSecondary, textTransform: 'lowercase', letterSpacing: '1px' }}>active workspace</span>
                </div>
             </div>
             
             <div
              className="gradient-border-wrapper active"
              style={{
                // Inline fallback — guarantees the wrapper expands to fill
                // its parent flex column even if the .gradient-border-wrapper
                // <style> rules haven't applied yet. Without this, the
                // container collapses to 0 height in production paint and
                // the "click to expand" pill (absolutely-positioned to
                // bottom:12px) ends up at the top edge.
                flex: 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '18px',
                boxShadow: isDarkMode 
                  ? '0 0 12px rgba(79, 195, 247, 0.2)'
                  : '0 0 10px rgba(79, 195, 247, 0.12)',
                padding: '3px',
              }}
             >
             <div
              onClick={() => onPreviewClick && onPreviewClick(displayPreviewModalId)}
              className="gradient-border-inner"
              style={{
                // Inline fallback — fills the gradient-border-wrapper.
                flex: 1,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                borderRadius: '16px',
                background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
              }}
            >
              <div ref={tabletPreviewRef} className="dq-prev-body" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                  <div style={{
                    /* Same canvas colour as the dashboards: short pages now sit on an
                       explicit full-height logical canvas (added so loaders fill the
                       visible crop), so paint it to match or the frame backdrop shows. */
                    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
                    transform: `scale(${tabletPreviewScale})`,
                    transformOrigin: 'top left',
                    width: '1400px',  /* fixed logical width — dashboard always lays out at this size */
                    /* Explicit logical height = visible window / scale, so fixed
                       overlays (loaders) fill exactly the visible crop. */
                    height: tabletPreviewScale ? `${100 / tabletPreviewScale}%` : '100%',
                    overflow: 'hidden',
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}>
                    <div className="preview-transparent-mode" style={{ pointerEvents: 'none' }}>{previewContent}</div>
                  </div>
              </div>
              
              <div className="dq-prev-shade" style={{
                position: 'absolute', inset: 0, 
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '24px',
              }}>
                 {/* Persistent subtle hint */}
                 <div className="dq-prev-hint" style={{
                   position: 'absolute', bottom: '12px', right: '12px',
                   padding: '5px 10px', 
                   backgroundColor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.45)',
                   backdropFilter: 'blur(8px)',
                   color: 'rgba(255,255,255,0.8)', borderRadius: '8px',
                   fontSize: '11px', fontWeight: '500',
                   
                   display: 'flex', alignItems: 'center', gap: '5px'
                 }}>
                   <Zap size={11} />
                   <span>Click to expand</span>
                 </div>
                 
                 <div className="dq-resume-cta" style={{
                   padding: '10px 24px', backgroundColor: colors.accent, color: 'white', borderRadius: '30px',
                   fontSize: '14px', fontWeight: '600', boxShadow: `0 4px 20px ${colors.accent}60`,
                    display: 'flex', alignItems: 'center', gap: '8px',
                   position: 'relative', overflow: 'hidden',
                 }}>
                   <span>Resume Work</span>
                   <Zap size={16} fill="white" />
                 </div>
              </div>
             </div>
             </div>
          </div>
        )}
        
        {/* Welcome Landing - tablet (when no preview) */}
        {!showPreview && !activeModal && (
          <div style={{ flex: '1', padding: '60px 24px 40px 0', zIndex: 5, display: 'flex', alignItems: projectLoaded ? 'flex-start' : 'center', justifyContent: projectLoaded ? 'flex-start' : 'center', overflowY: 'auto' }}>
            <WelcomeLanding
              userName={userName}
              userPicture={userPicture}
              projectLoaded={projectLoaded}
              projectName={projectName}
              dataLoaded={dataLoaded}
              qualityChecksRun={qualityChecksRun}
              cleaningComplete={cleaningComplete}
              onOpenModal={onOpenModal}
              onProjectSelect={onProjectSelect}
            />
          </div>
        )}
        
        {/* Bottom Bar - Centered: controls + powered by */}
        <UserDock
          brandLogo={isDarkMode ? logoSourceInverted : logoSource}
          userName={userName}
          userPicture={userPicture}
          isDarkMode={isDarkMode}
          colors={colors}
          toggleTheme={toggleTheme}
          onSettings={() => settingsNavigate('/settings')}
          onLogout={onLogout}
          poweredByLogo={nobleLogo}
        />
        
        <style>{`
          .preview-transparent-mode > div { background: ${isDarkMode ? 'rgba(11,12,14,0.5)' : 'transparent'} !important; }
          .preview-transparent-mode .maindashboard-container > div { background: ${isDarkMode ? 'rgba(11,12,14,0.5)' : 'transparent'} !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; box-shadow: none !important; }
          .preview-transparent-mode ::-webkit-scrollbar { display: none; }
          /* The transparent-mode rules above strip backgrounds so the preview
             shows the frame canvas -- but the loading overlay NEEDS its
             backdrop, or a run-in-progress renders as floating text over the
             page (invisible in light mode, half-washed in dark). Re-assert it
             with higher specificity than both transparent rules. */
          .preview-transparent-mode .ailo-root,
          .preview-transparent-mode .maindashboard-container .ailo-root {
            background: ${isDarkMode
              ? 'radial-gradient(ellipse 80% 60% at 50% 45%, #11141A 0%, #0A0B0D 60%, #07080A 100%)'
              : 'radial-gradient(ellipse 80% 60% at 50% 45%, #FFFFFF 0%, #F2F4F7 60%, #E9ECF1 100%)'} !important;
          }
          
          @property --border-angle {
            syntax: '<angle>';
            initial-value: 0deg;
            inherits: false;
          }
          
          @keyframes borderRotate {
            to { --border-angle: 360deg; }
          }
          
          .gradient-border-wrapper {
            flex: 1;
            flex-direction: column;
            --border-angle: 0deg;
            position: relative;
            padding: 2px;
            border-radius: 22px;
            display: flex;
            transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease;
            will-change: transform;
          }
          
          .gradient-border-wrapper::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 22px;
            padding: 2px;
            background: ${colors.nodeBorder};
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
            z-index: 2;
            transition: all 0.4s ease;
          }
          
          .gradient-border-wrapper:hover {
            transform: scale(1.004);
            box-shadow: ${isDarkMode 
              ? '0 0 18px rgba(79, 195, 247, 0.28)'
              : '0 0 14px rgba(79, 195, 247, 0.18)'};
          }
          
          .gradient-border-wrapper:hover::before {
            padding: 2px;
            background: conic-gradient(
              from var(--border-angle),
              ${isDarkMode ? '#38B6FF, #C8923C, #0A0A0Bff, #0A0A0Bff, #38B6FF' : '#2196F3, #C8923C, #FAFAFAff, #FAFAFAff, #2196F3'}
            );
            animation: borderRotate 3s linear infinite;
          }
          
          .gradient-border-inner {
            background: ${isDarkMode ? '#0D0E0E' : '#F5F5F5'}
          /* CSS-only hover: no React state, so hovering never re-renders the
             (heavy, scaled) preview subtree */
          .dq-prev-body { opacity: 0.8; transition: opacity 0.3s ease; }
          .gradient-border-wrapper:hover .dq-prev-body { opacity: 1; }
          .dq-prev-shade {
            background: linear-gradient(to top, rgba(0,0,0,0.3), transparent 25%);
            transition: background 0.3s ease;
          }
          .gradient-border-wrapper:hover .dq-prev-shade {
            background: linear-gradient(to top, rgba(0,0,0,0.75), transparent 50%);
          }
          .dq-prev-hint { opacity: 0.85; transition: opacity 0.3s ease; }
          .gradient-border-wrapper:hover .dq-prev-hint { opacity: 0; }
          .dq-resume-cta {
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 0.3s ease, transform 0.3s ease;
            pointer-events: none;
          }
          .gradient-border-wrapper:hover .dq-resume-cta {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }
;
            flex: 1;
            border-radius: 20px;
            position: relative;
            overflow: hidden;
            cursor: pointer;
            z-index: 1;
          }
          
          .pipeline-center-scroll {
            scrollbar-width: thin;
            scrollbar-color: transparent transparent;
          }
          .pipeline-center-scroll:hover {
            scrollbar-color: ${isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'} transparent;
          }
          .pipeline-center-scroll::-webkit-scrollbar { width: 6px; }
          .pipeline-center-scroll::-webkit-scrollbar-track { background: transparent; }
          .pipeline-center-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 3px; }
          .pipeline-center-scroll:hover::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}; }
          .pipeline-center-scroll:hover::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'}; }
        `}</style>
      </div>
    );
  }
 
  
  // DESKTOP LAYOUT — Menu (left) → Workspace (center) → Guide (right)
  
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: colors.bg, display: 'flex', zIndex: 1,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      overflow: 'hidden'
    }}>
      <AuroraBackground 
        blobCount={5} animationSpeed={0.3} blurAmount={120}
        saturation={isDarkMode ? 70 : 60} baseOpacity={isDarkMode ? 0.3 : 0.4}
      />
 
      {/* Project Name - Centered Top */}
      {projectLoaded && projectName && (
        /* Quiet centred wordmark instead of a boxed pill: eyebrow + name +
           a hairline gradient tick. Reads as a title, not a button. */
        <div style={{
          position: 'fixed',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '3px',
          maxWidth: '350px',
          pointerEvents: 'none',
        }}>
          <span style={{
            fontSize: '9px',
            fontWeight: 500,
            letterSpacing: '0.22em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
            project
          </span>
          <span style={{ 
            fontSize: '13px', 
            fontWeight: '500', 
            letterSpacing: '-0.01em',
            color: colors.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
            {projectName}
          </span>
          <span aria-hidden="true" style={{
            width: '26px',
            height: '2px',
            borderRadius: '1px',
            background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`,
            opacity: 0.7,
          }} />
        </div>
      )}
 
      {/* ── LEFT: Pipeline Menu (hidden until project selected) ── */}
      {projectLoaded && (
      <div className="pipeline-center-scroll" style={{
        flex: '0 0 auto', display: 'flex', flexDirection: 'column',
        alignItems: sidebarCollapsed ? 'center' : 'flex-start',
        padding: sidebarCollapsed ? '52px 6px 40px 6px' : '52px 8px 40px 16px', 
        overflowY: 'auto', overflowX: 'hidden',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        width: sidebarCollapsed ? '52px' : '200px', 
        minWidth: sidebarCollapsed ? '52px' : '180px', 
        zIndex: 5,
        position: 'relative',
      }}>
        {/* Collapse/Expand Toggle */}
        <div
          role="button"
          tabIndex={0}
          onClick={toggleSidebar}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSidebar(); } }}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            position: 'absolute',
            top: '52px',
            right: sidebarCollapsed ? '50%' : '4px',
            transform: sidebarCollapsed ? 'translateX(50%)' : 'none',
            width: '22px', height: '22px', borderRadius: '6px',
            border: `1px solid ${colors.nodeBorder}`,
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            color: colors.textMuted,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10,
            transition: 'all 0.2s ease',
            opacity: 0.6,
            boxSizing: 'border-box',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
        >
          {sidebarCollapsed ? <ChevronsRight size={12} /> : <ChevronsLeft size={12} />}
        </div>
 
        {/* Pipeline List */}
        <div style={{ 
          display: 'flex', flexDirection: 'column', gap: '0px', paddingBottom: '80px', paddingTop: '20px',
          width: '100%',
          alignItems: sidebarCollapsed ? 'center' : 'stretch',
        }}>
          {pipeline.map((node, index) => renderPipelineNode(node, index))}
        </div>
      </div>
      )}
 
      {/* Bottom Bar - Centered: controls + powered by */}
      <UserDock
          brandLogo={isDarkMode ? logoSourceInverted : logoSource}
        userName={userName}
        userPicture={userPicture}
        isDarkMode={isDarkMode}
        colors={colors}
        toggleTheme={toggleTheme}
        onSettings={() => settingsNavigate('/settings')}
        onLogout={onLogout}
        poweredByLogo={nobleLogo}
      />
 
      {/* ── CENTER: Active Workspace (dominant) ── */}
      {showPreview && (
        <div style={{
          flex: '1',
          padding: '52px 16px 56px 12px', zIndex: 5, display: 'flex', flexDirection: 'column',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                fontFamily: "'Outfit', -apple-system, sans-serif",
              }}>
                <span style={{
                  width: '5px', height: '5px', borderRadius: '50%',
                  background: '#2563EB',
                  boxShadow: '0 0 6px rgba(37, 99, 235, 0.5)',
                  animation: 'dlBreathe 2.4s ease-in-out infinite',
                }} />
                <span style={{
                  fontSize: '10px',
                  fontWeight: 500,
                  color: colors.textMuted,
                  textTransform: 'lowercase',
                  letterSpacing: '0.16em',
                }}>
                  active workspace
                </span>
              </div>
           </div>
           
           <div
            className="gradient-border-wrapper"
           >
           <div
            onClick={() => onPreviewClick && onPreviewClick(displayPreviewModalId)}
            className="gradient-border-inner"
          >
            <div ref={desktopPreviewRef} className="dq-prev-body" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                <div style={{
                  /* Same canvas colour as the dashboards: short pages now sit on an
                     explicit full-height logical canvas (added so loaders fill the
                     visible crop), so paint it to match or the frame backdrop shows. */
                  background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
                  transform: `scale(${desktopPreviewScale})`,
                  transformOrigin: 'top left',
                  width: '1600px',  /* fixed logical width — dashboard always lays out at this size; transform handles all visual sizing */
                  /* Explicit logical height = visible window / scale, so fixed
                     overlays (loaders) fill exactly the visible crop. */
                  height: desktopPreviewScale ? `${100 / desktopPreviewScale}%` : '100%',
                  overflow: 'hidden',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                  <div className="preview-transparent-mode" style={{ pointerEvents: 'none' }}>{previewContent}</div>
                </div>
            </div>
            
            {/* Interactive Hover Overlay */}
            <div className="dq-prev-shade" style={{
              position: 'absolute', inset: 0, 
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '32px',
            }}>
               {/* Persistent subtle hint */}
               <div className="dq-prev-hint" style={{
                 position: 'absolute', bottom: '14px', right: '14px',
                 padding: '5px 11px',
                 backgroundColor: isDarkMode ? 'rgba(13, 14, 14, 0.7)' : 'rgba(26, 26, 26, 0.7)',
                 backdropFilter: 'blur(12px)',
                 WebkitBackdropFilter: 'blur(12px)',
                 color: 'rgba(255, 255, 255, 0.92)',
                 borderRadius: '100px',
                 fontSize: '10.5px',
                 fontWeight: 500,
                 letterSpacing: '-0.005em',
                 
                 display: 'flex',
                 alignItems: 'center',
                 gap: '5px',
                 fontFamily: "'Outfit', -apple-system, sans-serif",
               }}>
                 <Zap size={11} />
                 <span>click to expand</span>
               </div>
 
               {/* Full hover CTA — dark pill with brand-arc gradient on hover */}
               <div className="dq-resume-cta" style={{
                 padding: '10px 22px',
                 background: 'linear-gradient(135deg, #2563EB, #D97706)',
                 color: 'white',
                 borderRadius: '100px',
                 fontSize: '12.5px',
                 fontWeight: 500,
                 letterSpacing: '-0.005em',
                 //boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)',
                 
                 display: 'flex',
                 alignItems: 'center',
                 gap: '7px',
                 fontFamily: "'Outfit', -apple-system, sans-serif",
                 position: 'relative',
                 overflow: 'hidden',
               }}>
                 <span>resume work</span>
                 <Zap size={13} fill="white" />
               </div>
            </div>
           </div>
           </div>
        </div>
      )}
 
      {/* ── CENTER: Welcome Landing (when no active workspace) ── */}
      {!showPreview && !activeModal && (
        <div style={{
          flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 5, padding: '52px 24px 40px 16px', overflowY: 'auto',
          marginLeft: projectLoaded ? '-13%' : '0',
        }}>
          <WelcomeLanding
            userName={userName}
            userPicture={userPicture}
            projectLoaded={projectLoaded}
            projectName={projectName}
            dataLoaded={dataLoaded}
            qualityChecksRun={qualityChecksRun}
            cleaningComplete={cleaningComplete}
            onOpenModal={onOpenModal}
            onProjectSelect={onProjectSelect}
          />
        </div>
      )}
 
      {/* ── RIGHT: Quick Guide (dismissible companion) ── */}
      {showPreview && (
        <div style={{
          flex: `0 0 ${guideCollapsed ? '0px' : guideDetailed ? '26vw' : '22vw'}`,
          width: guideCollapsed ? '0px' : guideDetailed ? '26vw' : '22vw',
          padding: guideCollapsed ? '52px 0 40px 0' : '84px 24px 40px 0',
          zIndex: 5, overflowY: guideCollapsed ? 'hidden' : 'auto', overflowX: 'hidden',
          transition: 'flex-basis 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: guideCollapsed ? 'none' : 'auto',
        }}>
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.035)' : 'rgba(255, 255, 255, 0.72)',
            borderRadius: '14px',
            padding: '16px',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.07)'}`,
            boxShadow: isDarkMode
              ? '0 1px 2px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.25)'
              : '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.05)',
            transform: guideCollapsed ? 'scale(0.82)' : 'scale(1)',
            opacity: guideCollapsed ? 0 : 1,
            transformOrigin: 'top right',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease',
            minWidth: '200px',
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               gap: '10px',
               marginBottom: '14px',
               paddingBottom: '12px',
               borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
             }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '9px',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.055)' : 'rgba(0, 0, 0, 0.045)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isDarkMode ? '#60a5fa' : '#2563EB',
                  flexShrink: 0,
                }}>
                    <FileText size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: 500,
                    letterSpacing: '0.16em',
                    textTransform: 'lowercase',
                    color: colors.textMuted,
                    marginBottom: '2px',
                  }}>
                    quick guide
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '-0.005em',
                    color: colors.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {modalLabels[displayPreviewModalId]}
                  </div>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={toggleGuide}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGuide(); } }}
                  title="Dismiss guide"
                  style={{
                    width: '24px', height: '24px', borderRadius: '8px',
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
                    backgroundColor: 'transparent', color: colors.textMuted, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease', flexShrink: 0, boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = colors.text; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textMuted; }}
                >
                  <X size={12} />
                </div>
             </div>
            {/* Compact / Detailed toggle — macOS segmented control */}
            <div style={{
              display: 'flex',
              gap: '2px',
              padding: '3px',
              marginBottom: '14px',
              borderRadius: '10px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.055)' : 'rgba(0, 0, 0, 0.045)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.07)'}`,
            }}>
              {[
                { key: false, label: 'compact' },
                { key: true, label: 'detailed' },
              ].map(({ key, label }) => {
                const isOn = guideDetailed === key;
                return (
                  <div
                    key={label}
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (!isOn) toggleGuideDetail(); }}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isOn) { e.preventDefault(); toggleGuideDetail(); } }}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '5px 10px',
                      fontSize: '11px',
                      fontWeight: isOn ? 600 : 500,
                      letterSpacing: '0.06em',
                      textTransform: 'lowercase',
                      color: isOn ? colors.text : colors.textMuted,
                      backgroundColor: isOn
                        ? (isDarkMode ? '#17181a' : '#FFFFFF')
                        : 'transparent',
                      borderRadius: '7px',
                      boxShadow: isOn
                        ? (isDarkMode ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.08), 0 1px 6px rgba(0,0,0,0.06)')
                        : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      userSelect: 'none',
                    }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
            <ContextualDocumentation modalId={displayPreviewModalId} isDarkMode={isDarkMode} colors={colors} isDetailed={guideDetailed} />
          </div>
        </div>
      )}
 
      {/* Collapsed guide toggle button (right side) */}
      {showPreview && (
        <div
          role="button"
          tabIndex={0}
          onClick={toggleGuide}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGuide(); } }}
          title="Show Quick Guide"
          style={{
            position: 'fixed', top: '86px', right: '24px', zIndex: 15,
            width: '32px', height: '32px', borderRadius: '10px',
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.035)' : 'rgba(255, 255, 255, 0.72)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.07)'}`,
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            color: isDarkMode ? '#60a5fa' : '#2563EB', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'none',
            transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: guideCollapsed ? 1 : 0,
            transform: guideCollapsed ? 'scale(1)' : 'scale(0.7)',
            pointerEvents: guideCollapsed ? 'auto' : 'none',
            boxSizing: 'border-box',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.18)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = guideCollapsed ? 'scale(1)' : 'scale(0.7)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <HelpCircle size={14} />
        </div>
      )}
      
      {/* Styles for Preview Mode Transparency */}
      <style>{`
        .preview-transparent-mode > div { background: ${isDarkMode ? 'rgba(11,12,14,0.5)' : 'transparent'} !important; }
          .preview-transparent-mode .maindashboard-container > div { background: ${isDarkMode ? 'rgba(11,12,14,0.5)' : 'transparent'} !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; box-shadow: none !important; }
        .preview-transparent-mode ::-webkit-scrollbar { display: none; }
          /* The transparent-mode rules above strip backgrounds so the preview
             shows the frame canvas -- but the loading overlay NEEDS its
             backdrop, or a run-in-progress renders as floating text over the
             page (invisible in light mode, half-washed in dark). Re-assert it
             with higher specificity than both transparent rules. */
          .preview-transparent-mode .ailo-root,
          .preview-transparent-mode .maindashboard-container .ailo-root {
            background: ${isDarkMode
              ? 'radial-gradient(ellipse 80% 60% at 50% 45%, #11141A 0%, #0A0B0D 60%, #07080A 100%)'
              : 'radial-gradient(ellipse 80% 60% at 50% 45%, #FFFFFF 0%, #F2F4F7 60%, #E9ECF1 100%)'} !important;
          }
        
        @property --border-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        
        @keyframes borderRotate {
          to { --border-angle: 360deg; }
        }
 
        @keyframes dlBreathe {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.4); opacity: 0.55; }
        }
 
        /* Resume CTA — shimmer wave on hover (desktop + fine-pointer tablets) */
        @keyframes dqResumeShimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        .dq-resume-cta::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
          transform: translateX(-100%);
          pointer-events: none;
          z-index: 0;
        }
        .dq-resume-cta > * {
          position: relative;
          z-index: 1;
        }
        .dq-resume-cta svg {
          transition: transform 0.3s ease;
        }
        @media (hover: hover) and (pointer: fine) {
          .dq-resume-cta:hover::after {
            animation: dqResumeShimmer 1.2s ease-in-out;
          }
          .dq-resume-cta:hover {
            transform: translateY(-1px) !important;
          }
          .dq-resume-cta:hover svg {
            transform: translateX(4px);
          }
        }
        
        .gradient-border-wrapper {
          flex: 1;
          flex-direction: column;
          --border-angle: 0deg;
          position: relative;
          padding: 2px;
          border-radius: 15px;
          display: flex;
          transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease;
          will-change: transform;
        }
        
        .gradient-border-wrapper::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 15px;
          padding: 2px;
          background: ${colors.nodeBorder};
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          pointer-events: none;
          z-index: 2;
          transition: background 0.4s ease;
        }
        
        .gradient-border-wrapper:hover {
          transform: scale(1.004);
          box-shadow: ${isDarkMode 
            ? '0 0 18px rgba(79, 195, 247, 0.28)'
            : '0 0 14px rgba(79, 195, 247, 0.18)'};
        }
        
        .gradient-border-wrapper:hover::before {
          padding: 2px;
          background: conic-gradient(
            from var(--border-angle),
            ${isDarkMode ? '#38b6ff9d, #c8923c98, rgba(10, 10, 11, 0.53), rgba(10, 10, 11, 0.57), #38b6ffb2' : '#2159f3, #c8923c, rgb(250, 250, 250), rgb(250, 250, 250), #2195f3'}
          );
          animation: borderRotate 3s linear infinite;
        }
        
        .gradient-border-inner {
            background: ${isDarkMode ? '#0D0E0E' : '#eeeeee'}
          /* CSS-only hover: no React state, so hovering never re-renders the
             (heavy, scaled) preview subtree */
          .dq-prev-body { opacity: 0.8; transition: opacity 0.3s ease; }
          .gradient-border-wrapper:hover .dq-prev-body { opacity: 1; }
          .dq-prev-shade {
            background: linear-gradient(to top, rgba(0,0,0,0.3), transparent 25%);
            transition: background 0.3s ease;
          }
          .gradient-border-wrapper:hover .dq-prev-shade {
            background: linear-gradient(to top, rgba(0,0,0,0.75), transparent 50%);
          }
          .dq-prev-hint { opacity: 0.85; transition: opacity 0.3s ease; }
          .gradient-border-wrapper:hover .dq-prev-hint { opacity: 0; }
          .dq-resume-cta {
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 0.3s ease, transform 0.3s ease;
            pointer-events: none;
          }
          .gradient-border-wrapper:hover .dq-resume-cta {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }
;    /* inner preview background */
          flex: 1;
          border-radius: 13px;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          z-index: 1;
        }
        
        /* Scrollbar: hidden by default, visible on hover (Firefox-style) */
        .pipeline-center-scroll {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
          transition: scrollbar-color 0.3s ease;
        }
        .pipeline-center-scroll:hover {
          scrollbar-color: ${isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'} transparent;
        }
        .pipeline-center-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .pipeline-center-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .pipeline-center-scroll::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.3s ease;
        }
        .pipeline-center-scroll:hover::-webkit-scrollbar-thumb {
          background: ${isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
        }
        .pipeline-center-scroll:hover::-webkit-scrollbar-thumb:hover {
          background: ${isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'};
        }
      `}</style>
    </div>
  );
};
 
 
export default PipelineNavigation;